<template>
  <div class="page-table scrollable only-y call-history-plot-view" v-loading="loading">
    <div class="table-box page-header card-base card-shadow--medium">
      <div class="flex align-center mb-10 justify-space-between">
        <h2 class="mb-0 mt-0">
          <v-svg sprite="report" class="voxo-blue"></v-svg>
          Extension Chart
        </h2>
      </div>
      <div id="extension-plot" style="height: 385px; width: 100%"></div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import echarts from "echarts";

export default {
  name: "ExtensionPlot",
  props: {
    plotData: { required: true },
    loading: { required: true },
    maxTicks: { default: 9 },
    colors: {
      default: () => ["#13ce66", "#0F85FF", "#ff8f02", "#f79ca6", '#c485db'],
    },
  },
  data() {
    return {
      primaryLegend: [],
      chart: null,
      xInterval: 0,
      page: 1,
      pages: 0,
      limit: 3,
      plotMetrics: ["partnerExtensions", "partnerResidentialExtensions"],
      totalMetrics: ["extensions",],
      dateKeys: [],
      extensionRecords: [],
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    colorTheme() {
      return this.$store.state.layout.theme;
    },
  },
  watch: {
    plotData() {
      if (this.plotData) {
        this.doPreset();
      }
    },
    isResellerAccount() {
      return this.selectedTenant
        ? this.selectedTenant.isResellerAccount
        : false;
    },
  },
  methods: {

    doPreset() {
      this.buildChart();
    },
    buildX() {
      let dateKeys = this.plotData.map(item => moment(item.day).format("YYYY-MM-DD"));
      this.xInterval = Math.ceil(dateKeys.length / this.maxTicks);

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        data: dateKeys,
        boundaryGap: true,
        axisLabel: {
          interval: this.xInterval,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: axisColor,
          },
        },
        axisTick: {
          interval: this.xInterval,
        },
      };
    },
    buildY1() {
      if (this.selectedTenant.isResellerAccount) {
        const axisColor =
          this.colorTheme === "theme-dark"
            ? "rgba(255,255,255,0.5)"
            : "rgba(0,0,0, 0.5)";

        return {
          name: "Account",
          position: "left",
          splitLine: { show: false },
          axisLine: {
            lineStyle: {
              color: axisColor,
            },
          },
        };
      }
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        name: "Extensions",
        position: "left",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      };
    },
    buildY2() {
      if (!this.selectedTenant.isResellerAccount) {
        return {
          name: "",
          position: "right",
          splitLine: { show: false },
        }
      }
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        name: "Partner",
        position: "right",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        }
      }
    },
    buildPrimary(xAxis) {
      this.primaryLegend = [];
      const lineData = [...this.plotData];
      const nameMap = {
        extensions: "Extensions",
        partnerExtensions: "",
        partnerResidentialExtensions: ""
      };

      if (this.selectedTenant.isResellerAccount) {
        nameMap.extensions = "Account Extensions",
          nameMap.partnerExtensions = "Partner Extensions",
          nameMap.partnerResidentialExtensions = "Partner Residential Extensions"
      }

      let dataGroups = {};
      let totalGroup = {};
      if (this.selectedTenant.isResellerAccount) {
        for (let i = 0; i < this.plotMetrics.length; i++) {
          dataGroups[nameMap[this.plotMetrics[i]]] = [];
        }

        for (let i = 0; i < lineData.length; i++) {
          const currentExtension = lineData[i];
          for (let k = 0; k < this.plotMetrics.length; k++) {
            const currentMetric = this.plotMetrics[k];
            dataGroups[nameMap[currentMetric]].push(currentExtension[currentMetric]);
          }
        }
      }

      for (let i = 0; i < this.totalMetrics.length; i++) {
        totalGroup[nameMap[this.totalMetrics[i]]] = [];
      }

      for (let i = 0; i < lineData.length; i++) {
        const currentExtension = lineData[i];
        for (let k = 0; k < this.totalMetrics.length; k++) {
          const totalMetric = this.totalMetrics[k];
          totalGroup[nameMap[totalMetric]].push(currentExtension[totalMetric]);
        }
      }



      let series = [];
      let count = 0;
      let yIndex = 0;
      for (let group in dataGroups) {
        if (dataGroups.hasOwnProperty(group)) {
          //ternary to determine if yAxisIndex should be 1 or 0
          yIndex = group === 'Total' ? 1 : 0;
          series.push({
            name: `${group}`,
            type: "line",
            symbol: "circle",
            smooth: true,
            showSymbol: true,
            yAxisIndex: 1,
            symbolRepeat: true,
            symbolSize: 3,
            symbolMargin: 1,
            barMaxWidth: 5,
            z: -10,
            lineStyle: {
              color: this.colors[count],
            },
            itemStyle: {
              color: this.colors[count],
              borderColor: this.colors[count],
              borderWidth: 3,
            },
            data: dataGroups[group],
          });
          this.primaryLegend.push({ name: `${group}` });
          count++;
        }
      }
      for (let group in totalGroup) {
        if (totalGroup.hasOwnProperty(group)) {
          //ternary to determine if yAxisIndex should be 1 or 0
          yIndex = group === 'Total' ? 1 : 0;
          series.push({
            name: `${group}`,
            type: "bar",
            symbol: "circle",
            smooth: true,
            showSymbol: true,
            yAxisIndex: yIndex,
            symbolRepeat: true,
            symbolSize: 3,
            symbolMargin: 1,
            barMaxWidth: 5,
            z: -10,
            lineStyle: {
              color: this.colors[count],
            },
            itemStyle: {
              color: this.colors[count],
              borderColor: this.colors[count],
              borderWidth: 3,
            },
            data: totalGroup[group],
          });
          this.primaryLegend.push({ name: `${group}` });
          count++;
        }
      }
      return series;
    },
    buildChart() {
      if (this.chart) {
        this.chart.dispose();
      }

      this.chart = echarts.init(this.chartElement);

      const xAxis = this.buildX();
      const y1 = this.buildY1();
      const y2 = this.buildY2();
      const yAxis = y2 ? [y1, y2] : [y1];

      const seriesData = this.buildPrimary(xAxis.data);

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      this.chart.setOption({
        grid: {},
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        xAxis,
        yAxis,
        series: seriesData,
        legend: {
          show: true,
          type: "scroll",
          orient: "horizontal",
          icon: "rectangle",
          itemWidth: 15,
          itemHeight: 3,
          data: this.primaryLegend,
          textStyle: {
            color: axisColor,
          },
        },
      });
    }
  },
  mounted() {
    this.chartElement = document.getElementById("extension-plot");
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
};
</script>
